import {
  BarChartLegend,
  BarData,
  BarGroupData,
  BarPart,
  BarX
} from '../../shared/models/charts/chart-data-types';
import { NoRoundPercentsPipe } from '../../profile/pipes/no-round-percents.pipe';
import { colors } from '../../gameday/colors';
import { ColorPartExtractor } from '../../gameday/mappers/gameDay.extractors';
import {
  Part,
  Tranche,
  TrancheGroup
} from '../../gameday/data/gameDay-data.transformations';
import { GameDayModel } from '../../models/gameday-model';

export function colorizeTrancheGroups(
  trancheGroups: TrancheGroup[],
  colorExtractor: ColorPartExtractor
): void {
  trancheGroups.forEach((trancheGroup: TrancheGroup) => {
    trancheGroup.tranches.forEach((tranche: Tranche) => {
      tranche.parts.forEach((part: Part) => {
        part.color = colorExtractor(part);
      });
    });
    trancheGroup.parts.forEach((part: Part) => {
      part.color = colorExtractor(part);
    });
  });
}

export function sortTrancheGroups(
  trancheGroups: TrancheGroup[],
  legends: BarChartLegend[]
): void {
  trancheGroups.forEach((trancheGroup: TrancheGroup) => {
    trancheGroup.tranches.forEach((tranche: Tranche) => {
      tranche.parts.sort((part1, part2) => {
        const index1 = legends.findIndex((legend) => legend.id === part1.id);
        const index2 = legends.findIndex((legend) => legend.id === part2.id);
        return index1 - index2;
      });
    });
    trancheGroup.parts.sort((part1, part2) => {
      const index1 = legends.findIndex((legend) => legend.id === part1.id);
      const index2 = legends.findIndex((legend) => legend.id === part2.id);
      return index1 - index2;
    });
  });
}

class XFuncResult implements BarX {
  constructor(public idx: number, public value: string) {}

  toString(): string {
    return `${this.value} ${this.idx}`;
  }
}

export function xFunc(input: { id: string }, idx: number): BarX {
  return new XFuncResult(idx, input.id);
}

export function getBarPartsFromTranche(
  tranche: Tranche,
  tranchesLength: number,
  trancheIndex: number,
  x: BarX
): BarPart[] {
  const parts: BarPart[] = tranche.parts.reduce(
    (barParts: BarPart[], part: Part, partIndex: number) => {
      barParts.push({
        id: tranche.id,
        x,
        y: (partIndex ? barParts[partIndex - 1].y : 0) + part.amount,
        y0: partIndex ? barParts[partIndex - 1].y : 0,
        barsCount: tranchesLength,
        barIdx: trancheIndex,
        color: part.color
      });
      return barParts;
    },
    []
  );
  if (parts.length === 0 || parts[parts.length - 1].y < tranche.size) {
    parts.push({
      id: tranche.id,
      x,
      y: tranche.size,
      y0: parts.length ? parts[parts.length - 1].y : 0,
      barsCount: tranchesLength,
      barIdx: trancheIndex,
      color: colors.special.expected
    });
  }
  return parts;
}

export function trancheToBar(
  tranche: Tranche,
  tranchesLength: number,
  trancheIndex: number,
  x: BarX,
  selected: boolean,
  orderPeriodOpen: boolean
): BarData {
  const parts = getBarPartsFromTranche(
    tranche,
    tranchesLength,
    trancheIndex,
    x
  );
  return {
    topLabel: tranche.topLabel,
    id: tranche.id,
    bottomLabel: tranche.bottomLabel,
    selected,
    barsCount: tranchesLength,
    barIdx: trancheIndex,
    parts,
    extra: [
      {
        x,
        y: tranche.size,
        barsCount: tranchesLength,
        barIdx: trancheIndex
      }
    ],
    isOrderPeriodTerminated: orderPeriodOpen && isOrderPeriodTerminated(tranche)
  };
}

export function trancheGroupsToBarGroups(
  trancheGroups: TrancheGroup[],
  selectedTrancheId = '',
  combinedTranchesGroupsIds: string[] = [],
  highlightedTrancheId?: string,
  orderPeriodOpen?: boolean
): BarGroupData[] {
  return trancheGroups.map(
    (trancheGroup: TrancheGroup, trancheGroupIndex: number) => {
      const combined =
        combinedTranchesGroupsIds.indexOf(trancheGroup.id) > -1 &&
        trancheGroup.tranches.length > 1;
      let bars: BarData[];
      const x = xFunc(trancheGroup, trancheGroupIndex);
      let barsCount;
      let expanded =
        trancheGroup.id === selectedTrancheId ||
        trancheGroup.tranches.some(
          (tranche) => tranche.id === selectedTrancheId
        );
      if (combined) {
        expanded = trancheGroup.id === selectedTrancheId;
        bars = [
          {
            topLabel: trancheGroup.topLabel,
            id: trancheGroup.id,
            bottomLabel: trancheGroup.bottomLabel,
            selected: expanded,
            barsCount: 1,
            barIdx: 0,
            parts: getBarPartsFromTranche(trancheGroup, 1, 0, x),
            extra: [
              {
                x,
                y: trancheGroup.size,
                barsCount: 1,
                barIdx: 0
              }
            ],
            isOrderPeriodTerminated:
              !!orderPeriodOpen &&
              isOrderPeriodsTerminatedForTrancheGroup(
                trancheGroup && trancheGroup.tranches
              )
          }
        ];
        barsCount = 1;
      } else {
        bars = trancheGroup.tranches.map(
          (tranche: Tranche, trancheIndex: number, tranches: Tranche[]) =>
            trancheToBar(
              tranche,
              tranches.length,
              trancheIndex,
              x,
              tranche.id === selectedTrancheId,
              !!orderPeriodOpen
            )
        );
        barsCount = trancheGroup.tranches.length;
      }
      return {
        topLabel: trancheGroup.topLabel,
        id: trancheGroup.id,
        bottomLabel: trancheGroup.bottomLabel,
        expanded,
        combined,
        barsCount,
        x,
        bars,
        highlighted: highlightedTrancheId === trancheGroup.id
      };
    }
  );
}

const toNoRoundPercents = new NoRoundPercentsPipe().transform;

export function setValuesOnLegends(
  legends: BarChartLegend[],
  trancheGroups: TrancheGroup[]
): void {
  const mapObject = {};
  let total = 0;
  trancheGroups.forEach((trancheGroup: TrancheGroup) => {
    trancheGroup.tranches.forEach((tranche: Tranche) => {
      tranche.parts.forEach((part: Part) => {
        const mapObjectValue = mapObject[part.id];
        if (mapObjectValue) {
          mapObject[part.id] += part.amount;
        } else {
          mapObject[part.id] = part.amount;
        }
        total += part.amount;
      });
    });
  });
  legends.forEach((legend) => {
    legend.value = toNoRoundPercents(
      ((mapObject[legend.id] || 0) / (total || 1)) * 100
    );
  });
}

/**
 * Used for determining that a tranche group is closed by looping through multiple tranches with multiple maturities
 */
function isOrderPeriodsTerminatedForTrancheGroup(tranches: Tranche[]): boolean {
  return tranches
    .reduce(
      (prev: GameDayModel.Maturity[], tranche: Tranche) =>
        tranche && tranche.maturities
          ? [...prev, ...tranche.maturities]
          : [...prev],
      []
    )
    .every(
      (maturity: GameDayModel.Maturity) =>
        !!(maturity && maturity.isOrderPeriodTerminated)
    );
}

/**
 * Used for determining a tranch is closed for one tranche with multiple maturities
 */
function isOrderPeriodTerminated(tranche: Tranche): boolean {
  return !!(
    tranche &&
    tranche.maturities &&
    tranche.maturities.every(
      (maturity) => !!(maturity && maturity.isOrderPeriodTerminated)
    )
  );
}

export function setSelectionsOnLegends(
  legends: BarChartLegend[],
  legendFilters: string[]
): void {
  const legendFilter: Record<string, unknown> = legendFilters.reduce(
    (prev: Record<string, unknown>, filter: string) => ({
      ...prev,
      [filter]: filter
    }),
    {}
  );

  legends.forEach((legend: BarChartLegend) => {
    if (legendFilter[legend.id]) {
      legend.selected = false;
    } else {
      legend.selected = true;
    }
  });
}

export function getCorrectedSelectedTrancheId(
  selectedTrancheId: string,
  combinedTranchesIds: string[],
  trancheGroups: TrancheGroup[]
): string {
  //split selected trancheGroup
  const selectedTrancheGroup = trancheGroups.find(
    (trancheGroup) => trancheGroup.id === selectedTrancheId
  );
  if (
    combinedTranchesIds.indexOf(selectedTrancheId) === -1 &&
    selectedTrancheGroup
  ) {
    return selectedTrancheGroup.tranches[0].id;
  }

  //combine selected tranche
  let selectedTranche;
  const trancheGroupOfSelectedTranche = trancheGroups.find((trancheGroup) =>
    trancheGroup.tranches.some((tranche) => {
      if (tranche.id === selectedTrancheId) {
        selectedTranche = tranche;
        return true;
      }
      return false;
    })
  );
  if (
    selectedTranche &&
    combinedTranchesIds.indexOf(trancheGroupOfSelectedTranche.id) > -1
  ) {
    return trancheGroupOfSelectedTranche.id;
  }
  return selectedTrancheId;
}
